/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { IBenefitUploadModel } from "Interfaces/Benefits/IBenefitUploadModel";
import { ISubmitCsvFileResponse } from "Interfaces/Benefits/ISubmitCsvFileResponse";
import { IKeyText } from "Interfaces/IKeyText";
import { IUploadCsvFileValidation } from "Interfaces/SA/IUploadCSVFileRequest";

// Define the initial state for the manage codes slice
const initialState = {
  // Set the list of benefit names
  selectedBenefitName: "Any" as string,
  // Set the error for benefit names API
  benefitNamesLoadError: "",
  // Set the list of benefit name options
  benefitNamesOptionList: [] as IKeyText[],
  // Set the list of managed codes
  manageCodesList: [] as IBenefitUploadModel[],
  // Set the current page for managed codes list
  currentPage: 1,
  // Set the filtered count for managed codes list
  filteredCount: 0,
  // Set the page size for managed codes
  pageSize: 16,
  // Set the upload response for managed codes CSV
  uploadCsvResponse: {} as IUploadCsvFileValidation,
  // Set the submission status for managed codes CSV
  submitCsvFileResponse: {} as ISubmitCsvFileResponse,
};

// Create the award categories slice to hold state, reducers and selectors for award categories
export const manageCodesSlice = createSlice({
  name: "manageCodes",
  initialState,
  reducers: {
    setSelectedBenefitName: (state, action: PayloadAction<string>) => {
      state.selectedBenefitName = action.payload;
    },
    setBenefitNamesLoadError: (state, action: PayloadAction<string>) => {
      state.benefitNamesLoadError = action.payload;
    },
    setBenefitNamesOptionList: (state, action: PayloadAction<IKeyText[]>) => {
      state.benefitNamesOptionList = action.payload;
    },
    setManageCodesList: (state, action: PayloadAction<IBenefitUploadModel[]>) => {
      state.manageCodesList = action.payload;
    },
    setFilteredCount: (state, action: PayloadAction<number>) => {
      state.filteredCount = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setUploadCsvResponse: (state, action: PayloadAction<IUploadCsvFileValidation>) => {
      state.uploadCsvResponse = action.payload;
    },
    setSubmitCsvFileResponse: (state, action: PayloadAction<ISubmitCsvFileResponse>) => {
      state.submitCsvFileResponse = action.payload;
    },
  },
  selectors: {
    getSelectedBenefitName: (state) => state.selectedBenefitName, // Get the list of benefit names
    getBenefitNamesLoadError: (state) => state.benefitNamesLoadError, // Get the error for benefit names API 
    getBenefitNamesOptionList: (state) => state.benefitNamesOptionList, // Get the list of benefit name filter values
    getManageCodesList: (state) => state.manageCodesList, // Get the list of managed codes
    getFilteredCount: (state) => state.filteredCount, // Get the filtered count for managed codes list
    getCurrentPage: (state) => state.currentPage, // Get the current page for managed codes list
    getPageSize: (state) => state.pageSize, // Get the page size for managed codes
    getUploadCsvResponse: (state) => state.uploadCsvResponse, // Get the upload status for managed codes CSV
    getSubmitCsvFileResponse: (state) => state.submitCsvFileResponse, // Get the submission status for managed codes CSV
  },
});

export const {
  setSelectedBenefitName,
  setBenefitNamesLoadError,
  setBenefitNamesOptionList,
  setManageCodesList,
  setCurrentPage,
  setFilteredCount,
  setPageSize,
  setUploadCsvResponse,
  setSubmitCsvFileResponse,
} = manageCodesSlice.actions;

export const {
  getSelectedBenefitName,
  getBenefitNamesLoadError,
  getBenefitNamesOptionList,
  getManageCodesList,
  getFilteredCount,
  getCurrentPage,
  getPageSize,
  getUploadCsvResponse,
  getSubmitCsvFileResponse,
} = manageCodesSlice.selectors;

export default manageCodesSlice.reducer;
